import { getInitials } from '../../common/getInitials';

/**
 * Componente reutilizable para mostrar un avatar con iniciales
 * Incluye efectos de loading mejorados con spinner
 * 
 * @param {Object} props
 * @param {string} props.firstName - Primer nombre
 * @param {string} props.lastName - Apellido
 * @param {boolean} props.loading - Estado de carga
 * @param {string} props.size - Tamaño del avatar: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {string} props.className - Clases adicionales para el contenedor
 */
export const AvatarWithInitials = ({
    firstName = '',
    lastName = '',
    loading = false,
    size = 'md',
    className = ''
}) => {
    // Configuración de tamaños
    const sizeConfig = {
        sm: {
            container: 'w-14 h-14 md:w-16 md:h-16',
            border: 'border-2 md:border-3',
            text: 'text-xl md:text-2xl',
            spinner: 'w-6 h-6 md:w-7 md:h-7'
        },
        md: {
            container: 'w-14 h-14 md:w-20 md:h-20',
            border: 'border-2 md:border-4',
            text: 'text-xl md:text-[2rem]',
            spinner: 'w-6 h-6 md:w-8 md:h-8'
        },
        lg: {
            container: 'w-20 h-20',
            border: 'border-4',
            text: 'text-[2rem]',
            spinner: 'w-8 h-8'
        }
    };

    const config = sizeConfig[size] || sizeConfig.md;

    // Spinner SVG reutilizable
    const Spinner = ({ className: spinnerClassName = '' }) => (
        <svg className={spinnerClassName} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    );

    if (loading) {
        return (
            <div className={`${config.container} ${config.border} rounded-full border-white shadow-lg bg-gray-300 animate-pulse flex items-center justify-center ${className}`}>
                <Spinner className={`${config.spinner} text-gray-500 animate-spin`} />
            </div>
        );
    }

    const initials = getInitials(firstName, lastName);

    return (
        <div className={`${config.container} ${config.border} rounded-full border-white shadow-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center ${className}`}>
            {!initials ? (
                // Mostrar animación de carga si no hay iniciales válidas
                <Spinner className={`${config.spinner} text-white animate-spin`} />
            ) : (
                <span className={`${config.text} text-white font-bold`}>
                    {initials}
                </span>
            )}
        </div>
    );
};
